import React from 'react';
import { Link } from 'react-router-dom';

import { Category } from '../../entities/category';
import { strings } from '../../localization/strings';

interface ComponentProps {
  categories: Category[];
}

const typeLabels: Record<number, string> = {
  0: strings.categories.types.news,
  1: strings.categories.types.eltaGuide,
  2: strings.categories.types.expectedEvents,
};

const flattenCategories = (categories: Category[]): Category[] => {
  const childrenByParent = new Map<number | null, Category[]>();

  categories.forEach((category) => {
    const children = childrenByParent.get(category.parent_id) || [];

    children.push(category);
    childrenByParent.set(category.parent_id, children);
  });

  const sortCategories = (items: Category[]) =>
    items.sort((first, second) => {
      const orderDifference = (first.sort_order || 0) - (second.sort_order || 0);

      return orderDifference || first.name.localeCompare(second.name);
    });

  const flattened: Category[] = [];
  const appendChildren = (parentId: number | null, depth: number) => {
    sortCategories(childrenByParent.get(parentId) || []).forEach((category) => {
      flattened.push({ ...category, depth } as Category & { depth: number });
      appendChildren(category.id, depth + 1);
    });
  };

  appendChildren(null, 0);

  return flattened;
};

export default function CategoriesTableComponent({ categories }: ComponentProps) {
  const flattenedCategories = flattenCategories(categories);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              {strings.categories.columns.name}
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              {strings.categories.columns.type}
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              {strings.categories.columns.abbreviation}
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              {strings.categories.columns.abbreviationForeign}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {flattenedCategories.map((category) => {
            const { depth } = category as Category & { depth: number };

            return (
              <tr key={category.id} className={category.is_active ? undefined : 'bg-red-50'}>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                  <Link
                    to={`/categories/edit/${category.id}`}
                    className="text-indigo-600 hover:text-indigo-900"
                    style={{ paddingLeft: `${depth * 24}px` }}
                  >
                    {category.name}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                    {typeLabels[category.type] || category.type}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{category.abbreviation}</td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{category.abbreviation_foreign}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
